import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getAllOrders,
  getInvoiceLinks,
  sendInvoice,
  resendInvoice,
  markPaidOutsideStripe,
  mergeOrders,
} from '../../services/orders';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { PAYMENT_STATUS_LABELS } from '../../utils/constants';
import Loading from '../../components/common/Loading';
import Alert from '../../components/common/Alert';
import './Invoices.css';

const FILTERS = [
  { key: 'all',            label: 'All Invoiced' },
  { key: 'open',           label: 'Open' },
  { key: 'overdue',        label: 'Overdue' },
  { key: 'payment_failed', label: 'Payment Failed' },
  { key: 'paid',           label: 'Paid' },
  { key: 'voided',         label: 'Voided' },
  { key: 'no-invoice',     label: 'No Invoice' },
];

const STATUS_SORT = { overdue: 0, payment_failed: 0, open: 1, paid: 2, voided: 3 };

const InvoiceRow = ({ order: initialOrder, onUpdate, selectable = false, checked = false, onToggleSelect }) => {
  const [order, setOrder] = useState(initialOrder);
  const [links, setLinks] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [actionError, setActionError] = useState(null);
  const navigate = useNavigate();

  // Resync local state when the parent refetches fresh data for this row
  useEffect(() => {
    setOrder(initialOrder);
  }, [initialOrder.payment_status, initialOrder.stripe_invoice_id, initialOrder.invoice_sent_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = useCallback(async (key, fn) => {
    setActionLoading(key);
    setActionError(null);
    try {
      const result = await fn();
      if (result && result.id) {
        setOrder(result);
        setLinks(null); // clear cached links — payment status may have changed
        onUpdate?.(result);
      }
    } catch (err) {
      setActionError(err.message || 'Something went wrong.');
    } finally {
      setActionLoading(null);
    }
  }, [onUpdate]);

  const handleGetLinks = useCallback(async () => {
    setActionLoading('links');
    setActionError(null);
    try {
      const data = await getInvoiceLinks(order.id);
      setLinks(data);
    } catch (err) {
      setActionError(err.message || 'Failed to load links.');
    } finally {
      setActionLoading(null);
    }
  }, [order.id]);

  const status = order.payment_status || null;
  const hasInvoice = !!order.stripe_invoice_id;
  const isSent = !!order.invoice_sent_at;
  const busy = !!actionLoading;

  return (
    <div className={`emp-invoices__row${status ? ` emp-invoices__row--${status}` : ' emp-invoices__row--none'}${checked ? ' emp-invoices__row--selected' : ''}`}>

      {selectable && (
        <input
          type="checkbox"
          className="emp-invoices__row-check"
          checked={checked}
          onChange={() => onToggleSelect?.(order)}
          aria-label={`Select ${order.order_number} for merge`}
        />
      )}

      {/* Left: customer + order info */}
      <div className="emp-invoices__row-left">
        <div className="emp-invoices__row-top">
          <button
            className="emp-invoices__order-link"
            onClick={() => navigate(`/employee/orders/${order.id}`)}
          >
            {order.order_number}
          </button>
          {status && (
            <span className={`emp-invoices__badge emp-invoices__badge--${status}`}>
              {PAYMENT_STATUS_LABELS[status] || status}
            </span>
          )}
        </div>
        {order.company_name && (
          <div className="emp-invoices__customer">{order.company_name}</div>
        )}
        <div className="emp-invoices__row-sub">
          {order.session_detail?.label && <span>{order.session_detail.label}</span>}
          {order.session_detail?.label && (order.invoice_sent_at || order.due_date || order.payment_date) && (
            <span className="emp-invoices__dot">·</span>
          )}
          {order.payment_date && (
            <span className="emp-invoices__date-label">Paid {formatDate(order.payment_date)}</span>
          )}
          {!order.payment_date && order.due_date && (
            <span className={`emp-invoices__date-label${status === 'overdue' ? ' emp-invoices__date-label--overdue' : ''}`}>
              Due {formatDate(order.due_date)}
            </span>
          )}
          {!order.payment_date && !order.due_date && order.invoice_sent_at && (
            <span className="emp-invoices__date-label">Sent {formatDate(order.invoice_sent_at)}</span>
          )}
        </div>
      </div>

      {/* Right: amount + actions */}
      <div className="emp-invoices__row-right">
        <div className="emp-invoices__row-amount">
          {formatCurrency(order.total_amount_cents / 100)}
        </div>

        <div className="emp-invoices__row-actions">
          {!hasInvoice && (
            <button
              className="emp-invoices__action-btn"
              onClick={() => navigate(`/employee/orders/${order.id}`)}
            >
              Invoice on order →
            </button>
          )}

          {hasInvoice && !isSent && (
            <button
              className="emp-invoices__action-btn"
              disabled={busy}
              onClick={() => run('send', () => sendInvoice(order.id))}
            >
              {actionLoading === 'send' ? 'Sending…' : 'Send'}
            </button>
          )}

          {hasInvoice && isSent && status !== 'voided' && (
            <button
              className="emp-invoices__action-btn emp-invoices__action-btn--secondary"
              disabled={busy}
              onClick={() => run('resend', () => resendInvoice(order.id))}
            >
              {actionLoading === 'resend' ? 'Sending…' : 'Resend'}
            </button>
          )}

          {hasInvoice && (status === 'open' || status === 'overdue' || status === 'payment_failed') && (
            <button
              className="emp-invoices__action-btn emp-invoices__action-btn--secondary"
              disabled={busy}
              onClick={() => run('mark-paid', () => markPaidOutsideStripe(order.id))}
            >
              {actionLoading === 'mark-paid' ? 'Marking…' : 'Mark Paid'}
            </button>
          )}

          {hasInvoice && (
            links ? (
              <>
                {links.hosted_invoice_url && (
                  <a
                    href={links.hosted_invoice_url}
                    target="_blank"
                    rel="noreferrer"
                    className="emp-invoices__action-btn emp-invoices__action-btn--secondary"
                  >
                    View ↗
                  </a>
                )}
                {links.invoice_pdf && (
                  <a
                    href={links.invoice_pdf}
                    target="_blank"
                    rel="noreferrer"
                    className="emp-invoices__action-btn emp-invoices__action-btn--secondary"
                  >
                    PDF ↗
                  </a>
                )}
              </>
            ) : (
              <button
                className="emp-invoices__action-btn emp-invoices__action-btn--secondary"
                disabled={actionLoading === 'links'}
                onClick={handleGetLinks}
              >
                {actionLoading === 'links' ? 'Loading…' : 'Links'}
              </button>
            )
          )}
        </div>

        {actionError && (
          <div className="emp-invoices__row-error">{actionError}</div>
        )}
      </div>

    </div>
  );
};

const EmployeeInvoices = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [selected, setSelected] = useState([]); // orders selected for merge
  const [merging, setMerging] = useState(false);
  const [mergeError, setMergeError] = useState(null);
  const navigate = useNavigate();

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllOrders({ page_size: 500 });
      setOrders(data.results ?? data);
    } catch (err) {
      setError(err.message || 'Failed to load orders.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Clear any merge selection when leaving the "No Invoice" view.
  useEffect(() => {
    if (filter !== 'no-invoice') { setSelected([]); setMergeError(null); }
  }, [filter]);

  const handleOrderUpdate = useCallback((updated) => {
    setOrders(prev => prev.map(o => o.id === updated.id ? updated : o));
  }, []);

  const toggleSelect = useCallback((order) => {
    setMergeError(null);
    setSelected(prev =>
      prev.some(o => o.id === order.id)
        ? prev.filter(o => o.id !== order.id)
        : [...prev, order]
    );
  }, []);

  // A merge is valid only when 2+ orders of the same company are selected.
  const selectedCompanyIds = new Set(selected.map(o => o.company));
  const canMerge = selected.length >= 2 && selectedCompanyIds.size === 1;

  const handleMerge = useCallback(async () => {
    setMerging(true);
    setMergeError(null);
    try {
      const merged = await mergeOrders(selected.map(o => o.id));
      navigate(`/employee/orders/${merged.id}`);
    } catch (err) {
      setMergeError(err.message || 'Failed to merge orders.');
      setMerging(false);
    }
  }, [selected, navigate]);

  // Merged source orders are retired — never surface them in the invoice views.
  const visibleOrders = useMemo(
    () => orders.filter(o => o.status !== 'merged'),
    [orders]
  );

  // Distinct companies present in the visible orders, for the company filter.
  const companyOptions = useMemo(() => {
    const byId = new Map();
    for (const o of visibleOrders) {
      if (o.company != null && !byId.has(o.company)) {
        byId.set(o.company, o.company_name || `Company ${o.company}`);
      }
    }
    return [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [visibleOrders]);

  // Apply the company filter before status counts/sorting so both reflect it.
  const companyScoped = useMemo(
    () =>
      companyFilter === 'all'
        ? visibleOrders
        : visibleOrders.filter(o => String(o.company) === String(companyFilter)),
    [visibleOrders, companyFilter]
  );

  const counts = useMemo(() => {
    const c = { all: 0, open: 0, overdue: 0, payment_failed: 0, paid: 0, voided: 0, 'no-invoice': 0 };
    for (const o of companyScoped) {
      if (!o.stripe_invoice_id) { c['no-invoice']++; continue; }
      c.all++;
      if (o.payment_status in c) c[o.payment_status]++;
    }
    return c;
  }, [companyScoped]);

  const sorted = useMemo(() => {
    return companyScoped
      .filter((o) => {
        if (filter === 'no-invoice') return !o.stripe_invoice_id;
        if (filter === 'all')        return !!o.stripe_invoice_id;
        return !!o.stripe_invoice_id && o.payment_status === filter;
      })
      .sort((a, b) => {
        const aRank = STATUS_SORT[a.payment_status] ?? 4;
        const bRank = STATUS_SORT[b.payment_status] ?? 4;
        if (aRank !== bRank) return aRank - bRank;
        return new Date(b.created_at) - new Date(a.created_at);
      });
  }, [companyScoped, filter]);

  const selecting = filter === 'no-invoice';

  return (
    <div className="emp-invoices">
      <div className="emp-invoices__header">
        <h1 className="emp-invoices__title">Invoices</h1>
      </div>

      <hr className="emp-invoices__rule" />

      {loading && <div className="emp-invoices__loading"><Loading /></div>}
      {error   && <Alert type="error">{error}</Alert>}

      {!loading && !error && (
        <>
          <div className="emp-invoices__filters">
            {FILTERS.map(({ key, label }) => (
              <button
                key={key}
                className={`emp-invoices__filter-btn${filter === key ? ' emp-invoices__filter-btn--active' : ''}`}
                onClick={() => setFilter(key)}
              >
                {label}
                {counts[key] > 0 && (
                  <span className="emp-invoices__filter-count">{counts[key]}</span>
                )}
              </button>
            ))}
          </div>

          <div className="emp-invoices__company-filter">
            <label htmlFor="invoice-company-filter" className="emp-invoices__company-label">
              Company
            </label>
            <select
              id="invoice-company-filter"
              className="emp-invoices__company-select"
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
            >
              <option value="all">All companies</option>
              {companyOptions.map(({ id, name }) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
          </div>

          {selecting && (
            <div className="emp-invoices__merge-bar">
              <span className="emp-invoices__merge-hint">
                {selected.length === 0
                  ? 'Select two or more orders from the same company to merge them into one invoice.'
                  : `${selected.length} selected${
                      selectedCompanyIds.size > 1 ? ' — orders must be from the same company' : ''
                    }`}
              </span>
              <button
                className="emp-invoices__action-btn"
                disabled={!canMerge || merging}
                onClick={handleMerge}
              >
                {merging ? 'Merging…' : 'Merge selected'}
              </button>
            </div>
          )}

          {mergeError && <Alert type="error">{mergeError}</Alert>}

          {sorted.length === 0 ? (
            <p className="emp-invoices__empty">
              {filter === 'no-invoice' ? 'All orders have been invoiced.' : 'No invoices found.'}
            </p>
          ) : (
            <div className="emp-invoices__list">
              {sorted.map((order) => (
                <InvoiceRow
                  key={order.id}
                  order={order}
                  onUpdate={handleOrderUpdate}
                  selectable={selecting}
                  checked={selected.some(o => o.id === order.id)}
                  onToggleSelect={toggleSelect}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EmployeeInvoices;
